/**
 * Why a payment failed, in words somebody can act on.
 *
 * The reason has always been recorded — it arrives in the notification and is
 * stored on the purchase as `raw_ipn.message` — but it was only ever rendered
 * for notifications whose signature FAILED to verify, as part of a diagnostic
 * for that bug. Fixing the bug therefore removed the only place the reason was
 * visible, which is a strange way to lose a feature: the display was tied to
 * the fault rather than to the fact.
 *
 * This reads it from the record itself, so it works for every failed payment
 * and keeps working when nothing is broken.
 */

/** Gateway wording → what it means for the person who has to ring the customer.
 *
 *  Deliberately short. Only entries whose meaning is unambiguous are listed;
 *  anything else falls through and is shown verbatim, because a confident
 *  mistranslation of a bank's refusal is worse than the English. */
const GLOSS = {
  "transaction declined": "Refuzată de banca emitentă, fără motiv detaliat.",
  "insufficient funds": "Fonduri insuficiente pe card.",
  "not sufficient funds": "Fonduri insuficiente pe card.",
  "expired card": "Card expirat.",
  "do not honor": "Banca a refuzat tranzacția fără să spună de ce.",
  "do not honour": "Banca a refuzat tranzacția fără să spună de ce.",
  "invalid card number": "Număr de card greșit.",
  "incorrect cvv": "Cod CVV greșit.",
  "invalid cvv": "Cod CVV greșit.",
  "exceeds withdrawal limit": "Depășește limita cardului.",
  "restricted card": "Card restricționat pentru plăți online.",
  "lost card": "Card raportat pierdut.",
  "stolen card": "Card raportat furat.",
  "transaction not permitted": "Banca nu permite acest tip de tranzacție.",
  "3d secure failed": "Autentificarea 3D Secure nu a reușit.",
  "authentication failed": "Autentificarea 3D Secure nu a reușit.",
  "timeout": "Clientul nu a finalizat autentificarea în timp util.",
};

/** The raw wording from the gateway, or "" when the record holds none. */
export function rawReason(purchase) {
  const raw = purchase?.raw_ipn || purchase?.euplatesc_raw_response || null;
  const message = raw && typeof raw === "object" ? raw.message : null;
  return typeof message === "string" ? message.trim() : "";
}

/** A Romanian explanation, or "" when we do not have one we trust. */
export function glossFor(message) {
  if (!message) return "";
  return GLOSS[message.trim().toLowerCase()] || "";
}

/**
 * Everything the UI needs about a refusal, or `null` when there is nothing to
 * say — which is its own answer: a payment that failed with no reason recorded
 * usually means the person never reached the bank at all.
 */
export function failureReason(purchase) {
  const status = purchase?.status;
  if (status !== "failed" && status !== "cancelled") return null;
  const message = rawReason(purchase);
  if (!message) return null;
  return {
    message,
    gloss: glossFor(message),
    // The approval code is absent on every refusal; its presence alongside a
    // failure is a contradiction worth seeing rather than hiding.
    approval: purchase?.euplatesc_approval || null,
    cancelled: status === "cancelled",
  };
}
